import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  BarChart3, 
  Smartphone, 
  Globe,
  Zap,
  Shield,
  Users,
  Award,
  Play,
  Pause,
  Monitor,
  Tablet,
  Download,
  CheckCircle2,
  ArrowRight,
  Star
} from 'lucide-react';

// Import professional trading images
const platformDemoImage = require('../assets/diverse-group-business-professionals-posing-together-monitoring-room_482257-94131.jpg_kxystx.jpg');
const mobileAppImage = require('../assets/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg');
const analyticsDashboard = require('../assets/day-office-travel-agency_23-2150769936.jpg_ijmk2n.jpg');
const professionalTrading = require('../assets/teammates-working-late-office_23-2148991374.jpg_vty8zw.jpg');

const TradingPlatform = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [activeTab, setActiveTab] = useState('desktop');

  const platformFeatures = [
    {
      icon: <TrendingUp className="h-6 w-6 text-emerald-600" />,
      title: 'Advanced Charting',
      description: 'Professional-grade charts with 100+ technical indicators, drawing tools, and multiple timeframes for comprehensive market analysis.',
      highlights: ['100+ Technical Indicators', 'Multiple Chart Types', 'Custom Alerts', 'Pattern Recognition']
    },
    {
      icon: <BarChart3 className="h-6 w-6 text-blue-600" />,
      title: 'Market Research',
      description: 'In-depth market analysis, real-time news feeds, economic calendars, and expert insights to inform your trading decisions.',
      highlights: ['Real-time News', 'Economic Calendar', 'Analyst Reports', 'Market Screeners']
    },
    {
      icon: <Smartphone className="h-6 w-6 text-purple-600" />,
      title: 'Mobile Trading',
      description: 'Full platform functionality on your mobile device. Trade anywhere, anytime with our award-winning mobile applications.',
      highlights: ['iOS & Android Apps', 'Biometric Login', 'Push Notifications', 'Offline Charts']
    },
    {
      icon: <Globe className="h-6 w-6 text-orange-600" />,
      title: 'Global Markets',
      description: 'Access to global markets including stocks, cryptocurrencies, forex, commodities, and indices from a single platform.',
      highlights: ['150+ Global Markets', 'Multi-Asset Trading', '24/7 Market Access', 'Real-time Data']
    },
    {
      icon: <Zap className="h-6 w-6 text-green-600" />,
      title: 'Ultra-Fast Execution',
      description: 'Lightning-fast order execution with sub-millisecond latency and smart order routing for best price execution.',
      highlights: ['Sub-ms Execution', 'Smart Routing', 'No Slippage', '99.9% Uptime']
    },
    {
      icon: <Shield className="h-6 w-6 text-indigo-600" />,
      title: 'Bank-Grade Security',
      description: 'Your funds and data are protected with military-grade encryption, two-factor authentication, and segregated accounts.',
      highlights: ['256-bit Encryption', '2FA Security', 'Segregated Accounts', 'Insurance Protection']
    }
  ];

  const platformStats = [
    { number: '1M+', label: 'Active Users', icon: <Users className="h-6 w-6" /> },
    { number: '150+', label: 'Countries', icon: <Globe className="h-6 w-6" /> },
    { number: '$2B+', label: 'Daily Volume', icon: <BarChart3 className="h-6 w-6" /> },
    { number: '99.9%', label: 'Uptime', icon: <Zap className="h-6 w-6" /> }
  ];

  const platformTabs = [
    {
      id: 'desktop',
      label: 'Desktop Platform',
      icon: <Monitor className="h-5 w-5" />,
      description: 'Full-featured desktop application with advanced tools',
      image: platformDemoImage
    },
    {
      id: 'mobile',
      label: 'Mobile Apps',
      icon: <Smartphone className="h-5 w-5" />,
      description: 'Trade on the go with our award-winning mobile apps',
      image: mobileAppImage
    },
    {
      id: 'web',
      label: 'Web Platform',
      icon: <Globe className="h-5 w-5" />,
      description: 'Browser-based trading with no downloads required',
      image: analyticsDashboard
    }
  ];

  return (
    <section className="trading-platform-section">
      <div className="trading-platform-container">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="trading-platform-header"
        >
          <div className="trading-platform-badge">
            <Award className="trading-platform-badge-icon" />
            <span>Professional Trading Platform</span>
          </div>
          <h2 className="trading-platform-title">
            Experience the Future of
            <span className="trading-platform-title-gradient"> Trading Technology</span>
          </h2>
          <p className="trading-platform-subtitle">
            Our cutting-edge platform combines powerful technology with intuitive design 
            to deliver the ultimate professional trading experience across all devices.
          </p>
        </motion.div>

        {/* Platform Showcase */}
        <div className="mb-20">
          
          {/* Platform Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col lg:flex-row gap-4 mb-12 bg-white rounded-2xl p-2 shadow-lg"
          >
            {platformTabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center gap-3 p-4 rounded-xl transition-all duration-300 ${
                  activeTab === tab.id 
                    ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-lg' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {tab.icon}
                <div className="text-left">
                  <div className="font-semibold">{tab.label}</div>
                  <div className="text-sm opacity-80">{tab.description}</div>
                </div>
              </motion.button>
            ))}
          </motion.div>

          {/* Platform Demo */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            
            {/* Platform Screenshot/Demo */}
            <div className="relative">
              <div className="relative bg-white rounded-2xl shadow-2xl p-6">
                <div className="bg-slate-900 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-slate-800">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="text-white font-medium">
                      ChartLine {platformTabs.find(tab => tab.id === activeTab)?.label}
                    </div>
                    <div className="w-16"></div>
                  </div>
                  
                  <div className="relative h-80">
                    <img
                      src={platformTabs.find(tab => tab.id === activeTab)?.image}
                      alt={`${activeTab} platform`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                    
                    {/* Interactive Elements */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setIsPlaying(!isPlaying)}
                        className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
                      >
                        {isPlaying ? (
                          <Pause className="h-6 w-6" />
                        ) : (
                          <Play className="h-6 w-6" />
                        )}
                      </motion.button>
                    </div>

                    {/* Live Trading Indicators */}
                    <div className="absolute top-4 left-4 right-4">
                      <div className="flex items-center justify-between">
                        <div className="bg-emerald-500/90 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          LIVE TRADING
                        </div>
                        <div className="bg-white/90 text-slate-900 px-3 py-1 rounded-full text-sm font-semibold">
                          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Platform
                        </div>
                      </div>
                    </div>

                    {/* Floating Stats */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/20">
                          <div className="text-emerald-400 font-bold">+2.15%</div>
                          <div className="text-white text-xs">Portfolio</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/20">
                          <div className="text-blue-400 font-bold">$45.2K</div>
                          <div className="text-white text-xs">Balance</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/20">
                          <div className="text-yellow-400 font-bold">0.2ms</div>
                          <div className="text-white text-xs">Latency</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Badge */}
              {activeTab === 'mobile' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="absolute -bottom-6 -right-6 bg-gradient-to-r from-emerald-600 to-blue-600 text-white p-4 rounded-2xl shadow-xl"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Download className="h-5 w-5" />
                    <span className="font-semibold">Download App</span>
                  </div>
                  <div className="text-sm opacity-90">Available on iOS & Android</div>
                </motion.div>
              )}
            </div>

            {/* Platform Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">
                  {platformTabs.find(tab => tab.id === activeTab)?.label}
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  {platformTabs.find(tab => tab.id === activeTab)?.description}. 
                  Experience professional-grade trading tools designed for both beginners and expert traders.
                </p>
                
                {/* Key Features */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {['Real-time Data', 'Advanced Charts', 'One-Click Trading', 'Portfolio Analytics'].map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Platform Stats */}
              <div className="grid grid-cols-2 gap-6">
                {platformStats.slice(0, 4).map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-slate-50 rounded-xl p-4 text-center"
                  >
                    <div className="flex items-center justify-center mb-2 text-blue-600">
                      {stat.icon}
                    </div>
                    <div className="text-2xl font-bold text-slate-900 mb-1">{stat.number}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Try Platform Now
                  <ArrowRight className="h-4 w-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex-1 px-6 py-4 border border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all duration-300"
                >
                  Watch Demo
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Powerful Features for Every Trader
            </h3>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From beginners to professionals, our platform provides all the tools you need for successful trading
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {platformFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                
                <h4 className="text-xl font-bold text-slate-900 mb-4">
                  {feature.title}
                </h4>
                
                <p className="text-slate-600 leading-relaxed mb-6">
                  {feature.description}
                </p>

                <div className="space-y-2">
                  {feature.highlights.map((highlight, highlightIndex) => (
                    <div key={highlightIndex} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>



        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="trading-platform-cta"
        >
          <div className="trading-platform-cta-content">
            <h3 className="trading-platform-cta-title">
              Ready to Experience the Future of Trading?
            </h3>
            <p className="trading-platform-cta-subtitle">
              Join millions of traders who trust ChartLine for their trading success. 
              Start with our professional platform today.
            </p>
            <div className="trading-platform-cta-buttons">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="trading-platform-cta-primary"
              >
                Start Trading Now
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="trading-platform-cta-secondary"
              >
                Schedule Demo
              </motion.button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default TradingPlatform; 